import { createDmAdminProvider, DM_ADMIN_CONTRACT, dmRestrictionSeconds } from './dmModerationProvider';

// Admin-only domain adapter. Candidate source/spec provenance is explicit.
// Never fall back to the participant/persona DM client or mark-read API.
export class DmModerationUnavailable extends Error {
  constructor(message = 'DM 운영 API가 아직 연결되지 않았습니다. 내역·사용량·조치 결과를 확인할 수 없습니다.') {
    super(message);
    this.code = 'DM_ADMIN_UNAVAILABLE';
  }
}

const unavailable = () => { throw new DmModerationUnavailable(); };

export function validateDmPage(value) {
  if (!value || !Array.isArray(value.items) || !Object.prototype.hasOwnProperty.call(value, 'nextCursor') || (value.nextCursor !== null && (typeof value.nextCursor !== 'string' || !value.nextCursor))) {
    throw new DmModerationUnavailable('DM 페이지 응답이 제공되지 않거나 계약과 다릅니다.');
  }
  return value;
}

export function createDmModerationAdapter(provider) {
  const read = (name, validate = (value) => value) => async (input) => {
    if (typeof provider?.[name] !== 'function') return unavailable();
    return validate(await provider[name](input));
  };
  return {
    mode: provider ? 'api' : 'unavailable',
    contract: provider ? DM_ADMIN_CONTRACT : undefined,
    listMessages: read('listMessages', validateDmPage),
    listReports: read('listReports', validateDmPage),
    getRestriction: read('getRestriction'),
    getCase: read('getCase', (value) => {
      if (!value?.case || !Array.isArray(value.context) || !Array.isArray(value.actions)) return unavailable();
      return value;
    }),
    getUserHistory: read('getUserHistory', (value) => {
      validateDmPage(value);
      if (!value.summary || !value.reportPage || !value.actionPage) return unavailable();
      validateDmPage(value.reportPage);
      validateDmPage(value.actionPage);
      return value;
    }),
    applyAction: async (input) => {
      if (!String(input.reasonCode || '').trim()) throw new Error('운영 사유를 입력하세요.');
      if (input.action === 'restrict' && !dmRestrictionSeconds(input)) throw new Error('DM 제한 기간이 올바르지 않습니다.');
      if ((input.action === 'restrict' || input.action === 'lift') && (!Number.isSafeInteger(input.expectedRevision) || input.expectedRevision < 0)) throw new Error('현재 DM 제한 revision이 필요합니다.');
      if (['review', 'dismiss', 'violation'].includes(input.action) && (!input.caseId || input.expectedRevision == null)) throw new Error('대상 신고와 최신 revision이 필요합니다.');
      if (!['review', 'dismiss', 'violation', 'restrict', 'lift'].includes(input.action)) throw new Error('지원하지 않는 DM 조치입니다.');
      if (typeof provider?.applyAction !== 'function') return unavailable();
      const result = await provider.applyAction(input);
      if (!result?.auditId || !result.state || typeof result.state !== 'object') throw new DmModerationUnavailable('조치 응답의 현재 상태 또는 감사 ID를 확인하지 못했습니다. 최신 상태를 조회하세요.');
      return result;
    },
  };
}

export const dmModerationAdapter = createDmModerationAdapter(createDmAdminProvider());
